import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { TableHeader, TableProperties } from 'components/Table/types'
import {
  CollapseContainer,
  LicensesGroupContainer,
  LicensesGroupSummary,
  StyledTableInnerWrapper,
  StyledTableOuterWrapper,
} from './styles'
import { MobileSortBy, Table } from 'components/Table'
import { prettyLicenseDescription } from 'features/licenses/utils'
import type { LicenseType } from 'features/licenses/types'
import { isCharsThresholdMet } from 'components/Table/utils'
import LicensesGroupHeading from 'features/licenses/components/LicensesGroupHeading'
import { SortingRule } from 'react-table'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import {
  collapseStateSelector,
  importLicenseSelector,
} from 'features/licenses/selectors'
import {
  importLicenseActions,
  updateLicenesesCollapseState,
} from 'features/licenses/slice'

interface LicensesGroupProps extends TableProperties<LicenseType> {
  addEmptyGroup: (name: string) => void
  removeEmptyGroup: (name: string) => void
  headers: Record<string, TableHeader>
  sortOptions: SortingRule<LicenseType>[]
  sortBy: SortingRule<LicenseType>
  setSortBy: Dispatch<SetStateAction<SortingRule<LicenseType>>>
  showAvailableCodes: boolean
}

function LicensesGroup({
  columns,
  data,
  searchValue,
  defaultSortBy,
  addEmptyGroup,
  removeEmptyGroup,
  hiddenColumns,
  mobileViewComponent,
  headers,
  sortOptions,
  sortBy,
  setSortBy,
  showAvailableCodes,
}: LicensesGroupProps) {
  const dispatch = useAppDispatch()
  const [expand, setExpand] = useState(false)
  const [rowsCount, setRowsCount] = useState<number>()

  const collapseState = useAppSelector(collapseStateSelector)
  const newlyImportedLicense = useAppSelector(importLicenseSelector.data)
  const newlyImportedLicenseCode = newlyImportedLicense?.payload?.code

  const licenseGroupRef = useRef<null | HTMLDivElement>(null)

  // expand groups/dropdowns depending on search results and etc.
  useEffect(() => {
    const currentCollapseState = showAvailableCodes
      ? collapseState.available
      : collapseState.used

    setExpand(
      currentCollapseState.includes(data[0].name) ||
        isCharsThresholdMet(searchValue)
    )

    if (newlyImportedLicenseCode) {
      setExpand(
        data.some((license) => license.code === newlyImportedLicenseCode)
      )
      if (licenseGroupRef.current) {
        licenseGroupRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [
    collapseState.available,
    collapseState.used,
    data,
    dispatch,
    newlyImportedLicenseCode,
    searchValue,
    showAvailableCodes,
  ])

  // add group to empty-groups-list if no search results
  useEffect(() => {
    const groupName = data[0].name
    if (rowsCount === 0) {
      addEmptyGroup(groupName)
    } else {
      removeEmptyGroup(groupName)
    }
  }, [rowsCount, data, addEmptyGroup, removeEmptyGroup])

  useEffect(() => {
    return () => {
      if (newlyImportedLicenseCode) {
        dispatch(importLicenseActions.clear())
      }
    }
  }, [dispatch, newlyImportedLicenseCode, searchValue])

  const getRowsCount = useCallback((count: number) => setRowsCount(count), [])

  const isUsedLicensesData = data[0].activated || data[0].beginsAt

  const textForRowsCount =
    rowsCount && `${rowsCount} ${isUsedLicensesData ? 'used' : 'available'}`

  const collapse = useCallback(
    () =>
      dispatch(
        updateLicenesesCollapseState({
          showAvailableCodes,
          groupName: data[0].name,
        })
      ),
    [data, dispatch, showAvailableCodes]
  )

  // hide or show group based on how much rows was found after search
  const showGroup = useMemo(
    () => (isCharsThresholdMet(searchValue) ? !!rowsCount : true),
    [searchValue, rowsCount]
  )

  return (
    <LicensesGroupContainer showGroup={showGroup} ref={licenseGroupRef}>
      <LicensesGroupSummary onClick={collapse} collapse={expand}>
        <LicensesGroupHeading
          title={prettyLicenseDescription(data[0].description)}
          label={data[0].name}
          rowCount={textForRowsCount}
          chevronRotate={expand}
        />
      </LicensesGroupSummary>

      <CollapseContainer collapse={expand}>
        <StyledTableOuterWrapper>
          <StyledTableInnerWrapper>
            <MobileSortBy
              headers={headers}
              sortOptions={sortOptions}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
            <Table<LicenseType>
              columns={columns}
              data={data}
              searchValue={searchValue}
              defaultSortBy={defaultSortBy}
              rowsCountCallback={getRowsCount}
              hiddenColumns={hiddenColumns}
              mobileViewComponent={mobileViewComponent}
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          </StyledTableInnerWrapper>
        </StyledTableOuterWrapper>
      </CollapseContainer>
    </LicensesGroupContainer>
  )
}

export default memo(LicensesGroup)
